/* eslint-disable no-unused-vars */
export enum PermissionCode {
  CreateProfile = "CreateProfile",
  ModifyProfile = "ModifyProfile",
  ApproveDeclineKycStatus = "ApproveDeclineKycStatus",
  ExportProfile = "ExportProfile",
  DeleteProfile = "DeleteProfile",
  Screen = "Screen",
  AddToMonitoring = "AddToMonitoring",
  ModifyCriteria = "ModifyCriteria",
  ProcessMatches = "ProcessMatches",
  SetRiskRating = "SetRiskRating",
  SetStatus = "SetStatus",
  DeleteRecord = "DeleteRecord",
  RemoveFromMonitoring = "RemoveFromMonitoring",
  AddProfileToBusinessMonitoring = "AddProfileToBusinessMonitoring",
  ProcessBusinessMonitoringAlerts = "ProcessBusinessMonitoringAlerts",
  RemoveFromBusinessMonitoring = "RemoveFromBusinessMonitoring",
  PerformBatchOperations = "PerformBatchOperations",
  ConfigurePreferences = "ConfigurePreferences",
  AssignRoleToUsers = "AssignRoleToUsers",
  ConfigurePermissions = "ConfigurePermissions",
}
/* eslint-enable no-unused-vars */

export interface ApiPermissionItem {
  id: number;
  code: string;
  name: string;
  category: string;
  isAllowed: boolean;
}

export interface ApiPermissionsResponse {
  roleId: number;
  roleName: string;
  teamId: string;
  teamName: string;
  permissions: ApiPermissionItem[];
}

export type UserPermissions = Record<string, boolean>;

export interface UserPermissionsFetchResult {
  permissions: UserPermissions;
  teamId: string | null;
}

export type PermissionKey = PermissionCode | string;

export const DEFAULT_PERMISSIONS: UserPermissions = {};

const PERMISSION_CODE_BY_NORMALIZED_KEY = Object.fromEntries(
  Object.values(PermissionCode).map((code) => [code.toLowerCase(), code])
) as Record<string, PermissionCode>;

export const normalizePermissionCode = (apiCode: string): string =>
  PERMISSION_CODE_BY_NORMALIZED_KEY[apiCode.toLowerCase()] ?? apiCode;

/** Converts canonical PermissionCode values to the camelCase format returned by the API. */
export const permissionCodeToApiCode = (code: PermissionCode): string =>
  code.charAt(0).toLowerCase() + code.slice(1);

export const isPermissionGranted = (
  permissions: UserPermissions | undefined,
  code: string
): boolean => permissions?.[normalizePermissionCode(code)] ?? false;

export const transformApiPermissions = (
  apiResponse: ApiPermissionsResponse
): UserPermissions =>
  apiResponse.permissions.reduce<UserPermissions>((acc, item) => {
    acc[normalizePermissionCode(item.code)] = item.isAllowed;
    return acc;
  }, {});

export const mapApiPermissionsToFetchResult = (
  apiResponse: ApiPermissionsResponse
): UserPermissionsFetchResult => ({
  permissions: transformApiPermissions(apiResponse),
  teamId: apiResponse.teamId ?? null,
});

export interface PermissionsState {
  permissions: UserPermissions;
  teamId: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

export const INITIAL_PERMISSIONS_STATE: PermissionsState = {
  permissions: DEFAULT_PERMISSIONS,
  teamId: null,
  isLoaded: false,
  isLoading: false,
  error: null,
};

export interface PermissionCheckResult {
  hasPermission: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface GroupPermissions {
  teamId: string;
  groupName: string;
  permissions: UserPermissions;
}

export interface UserPermissionProfile {
  userId: number;
  permissions: UserPermissions;
  groups: GroupPermissions[];
  lastUpdated: string;
}
